import { createContext, useContext } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { format, parse } from 'date-fns'
import { camelCase, isEqual, unionWith } from 'lodash'

import type { DropdownOption } from '@acre/design-system'

import { ProductTypes } from '../fields/Case/sharedFields'
import { LEDGER_PAYMENT_TYPE_MAP } from '../pages/Cases/CaseDetails/CaseLedgers/CaseLedger.helpers'

export type ReportsContextStateType = {
  showViewByModal: boolean
  showFilterBar: boolean
  showThresholdView: boolean
  caseTypeOptions: Array<{ label: string; value: MortgageReason }>
  paymentStatusOptions: DropdownOption[]
  revenueTypeOptions: { label: string; value: LedgerType }[]
  viewDateByOptions: Array<{ label: string; value: string }>
  predictedRevenueTypeOptions: Array<{ label: string; value: string }>
  caseOwners?: GetCaseOwnersQuery | undefined
  arOrgs?: GetArOrganisationsMiQuery | undefined
  lenderNames?: GetLenderNamesQuery | undefined
  periodSettings?: GetOrganisationQuery['organisation']['periods_settings'] | undefined
}

export type ReportsContextType = {
  state?: ReportsContextStateType
  setState?: Dispatch<SetStateAction<ReportsContextStateType>>
}

// Acre currently does not support ReasonEquityRelease and ReasonFurtherAdvance case types
// they should only be available if the case in the system already exists with those types selected

const toCamelCase = <T extends string = string>(str: T) => camelCase(str)

let ViewDateByOptions: { label: string; value: ReportsViewDateBy }[] = [
  {
    label: `mi.viewDateBy.${toCamelCase<ReportsViewDateBy>('CASE_CREATED_AT')}`,
    value: 'CASE_CREATED_AT',
  },
  {
    label: `mi.viewDateBy.${toCamelCase<ReportsViewDateBy>('LEDGER_CREATED_AT')}`,
    value: 'LEDGER_CREATED_AT',
  },
  {
    label: `mi.viewDateBy.${toCamelCase<ReportsViewDateBy>('RECEIVED_DATE')}`,
    value: 'RECEIVED_DATE',
  },
]

let CaseTypeOptions = unionWith(
  [...ProductTypes],
  [
    {
      label: 'productTypes.noReasonProvided',
      value: 'INVALID_MORTGAGE_REASON',
    },
    {
      label: 'productTypes.furtherAdvance',
      value: 'REASON_FURTHER_ADVANCE',
    },
  ],
  (a, b) => isEqual(a.value, b.value),
)

const PredictedRevenueTypeOptions: { label: string; value: RevenueReportsType }[] = [
  {
    label: `mi.predictedRevenueTypes.${toCamelCase<RevenueReportsType>('REVENUE')}`,
    value: 'REVENUE',
  },
  {
    label: `mi.predictedRevenueTypes.${toCamelCase<RevenueReportsType>('PREDICTED_REVENUE')}`,
    value: 'PREDICTED_REVENUE',
  },
]

const revenueTypeCombinedCache: { [name: string]: true } = {}

const revenueTypeOptions = (Object.keys(LEDGER_PAYMENT_TYPE_MAP || {}) as LedgerType[]).sort().reduce(
  (acc, key: LedgerType) => {
    if (key.endsWith('PERCENTAGE')) {
      return acc
    }

    if (key.endsWith('COMBINED')) {
      revenueTypeCombinedCache[`${key.slice(0, -8)}FIXED`] = true
    }

    if (revenueTypeCombinedCache[key]) {
      return acc
    }

    acc.push({
      label: LEDGER_PAYMENT_TYPE_MAP[key],
      value: key,
    })

    return acc
  },
  [] as { label: string; value: LedgerType }[],
)

const paymentStatusOptions: DropdownOption<ReportsPaymentStatus>[] = [
  {
    label: 'accounting.statuses.DUE',
    value: 'OUTSTANDING',
  },
  {
    label: 'accounting.statuses.COLLECTED',
    value: 'COLLECTED',
  },
]

export const defaults: ReportsContextType = {
  state: {
    showViewByModal: false,
    showFilterBar: true,
    showThresholdView: true,
    caseTypeOptions: CaseTypeOptions,
    paymentStatusOptions,
    revenueTypeOptions,
    viewDateByOptions: ViewDateByOptions,
    predictedRevenueTypeOptions: PredictedRevenueTypeOptions,
    caseOwners: undefined,
    lenderNames: undefined,
  },
}

export const ReportsContext = createContext<ReportsContextType>(defaults)

export default ReportsContext

export const useReportsContext = () => useContext(ReportsContext)

export const getAdvisorOptions = (caseOwners: GetCaseOwnersQuery | undefined) => {
  return caseOwners?.caseOwners?.map((advisor) => ({
    value: advisor.id!,
    label: `${advisor.first_name} ${advisor.last_name}`,
  }))
}

export const getArOrgsOptions = (arOrgs: GetArOrganisationsMiQuery | undefined) =>
  arOrgs?.organisation?.arOrganisationsMi?.organisations?.length
    ? arOrgs?.organisation?.arOrganisationsMi?.organisations.reduce<{ label: string; value: string }[]>((acc, org) => {
        if (org.name && org.organisation_id) {
          return [
            ...acc,
            {
              label: org.name,
              value: org.organisation_id,
            },
          ]
        }
        return acc
      }, [])
    : []

export const getPeriodOptions = (periods?: Period[] | null) => {
  return periods?.map((period) => ({
    value: period.id!,
    label: period.name!,
    periodStartDate: period.start_date,
    periodEndDate: period.end_date,
  }))
}

export const getLenderOptions = (lenderNames: GetLenderNamesQuery | undefined) => {
  return lenderNames?.lenderNames?.map((lender) => ({
    value: lender.id!,
    label: lender.name!,
  }))
}

export const getName = (
  value?: string | null | undefined,
  viewBy?: ReportsViewBy,
  context?: ReportsContextType,
  formatMessage?: (id: string, values?: any) => string,
) => {
  if (viewBy === 'ADVISOR') {
    const advisor = context?.state?.caseOwners?.caseOwners?.find((advisor) => advisor.id === value)
    return advisor ? `${advisor.first_name} ${advisor.last_name}` : null
  }

  if (viewBy === 'LENDER') {
    return context?.state?.lenderNames?.lenderNames?.find((lender) => lender.id === value)?.name
  }

  if (viewBy === 'CASE_TYPE') {
    const label = context?.state?.caseTypeOptions?.find((caseType) => caseType.value === value)?.label
    return label && formatMessage ? formatMessage(label) : null
  }

  if (viewBy === 'INTRODUCER') {
    return null
  }

  if (viewBy === 'YEAR_MONTH') {
    return value ? format(parse(value, 'yyyy-M', new Date()), 'MMM yy') : ''
  }

  if (viewBy === 'LEDGER_TYPE') {
    let translationKey = value ? LEDGER_PAYMENT_TYPE_MAP[value as keyof typeof LEDGER_PAYMENT_TYPE_MAP] : ''

    if (value && value?.endsWith('Percentage')) {
      translationKey = translationKey + '%'
    }

    return translationKey && typeof formatMessage === 'function' ? formatMessage(translationKey) : translationKey
  }
}
